import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSalesService } from 'services/sales';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getCacheKeyWithCompany } from 'utils/cacheUtils';
import { Sales } from 'types/sales';

const SALES_CACHE_KEY_BASE = 'sales_cache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const getSalesCacheKey = (
  includeVoided: boolean,
  companyId: string | null | undefined,
) => {
  const baseKey = `${SALES_CACHE_KEY_BASE}_${includeVoided ? 'withVoided' : 'withoutVoided'}`;
  return getCacheKeyWithCompany(baseKey, companyId);
};

const sortSalesByDateDesc = (sales: Sales[]) =>
  [...sales].sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();

    if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
      return 0;
    }

    return bTime - aTime;
  });

interface UseSalesDataOptions {
  includeVoided: boolean;
}

interface UseSalesDataReturn {
  sales: Sales[];
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => void;
  updateSale: (sale: Sales) => void;
}

export function useSalesData(options: UseSalesDataOptions): UseSalesDataReturn {
  const { includeVoided } = options;
  const salesService = useSalesService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [sales, setSales] = useState<Sales[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create cache key with company ID suffix
  const salesCacheKey = useMemo(
    () => getSalesCacheKey(includeVoided, activeCompanyId),
    [includeVoided, activeCompanyId],
  );

  // Use localStorage hook with encryption and expiration
  const {
    data: cachedSales,
    setData: setCachedSales,
    isInitialized: cacheInitialized,
  } = useLocalStorage<Sales[]>({
    key: salesCacheKey,
    expirationMs: CACHE_EXPIRATION_MS,
    encrypt: true,
  });

  // Reset cache check when includeVoided or company changes (cache key changes)
  useEffect(() => {
    setCacheChecked(false);
    setLoading(true);
    // Clear sales temporarily while loading new cache
    setSales([]);
    // Reset error state
    setError(null);
  }, [includeVoided, activeCompanyId]);

  // Load cached data on mount or when company/cache key changes
  useEffect(() => {
    if (!cacheInitialized || !isCompanyInitialized) return; // Wait for both to be ready

    if (cachedSales) {
      // Use cached data - it's still valid (hook already checked expiration)
      setSales(sortSalesByDateDesc(cachedSales));
      setLoading(false);
      setCacheChecked(true);
    } else {
      // No cache or expired - mark as checked so we can fetch
      setCacheChecked(true);
    }
  }, [cachedSales, cacheInitialized, isCompanyInitialized, salesCacheKey]);

  const fetchSales = useCallback(
    async (isReload = false) => {
      // Don't fetch if company is not initialized yet
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      // Fetch fresh data (either cache expired, doesn't exist, or isReload is true)
      if (isReload) {
        setReloading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const fetchedSales = await salesService.getAllSales(includeVoided);
        const sortedSales = sortSalesByDateDesc(fetchedSales);
        setSales(sortedSales);

        // Limit cached sales to first 20 to avoid large data issues
        const salesToCache =
          sortedSales.length > 20 ? sortedSales.slice(0, 20) : sortedSales;

        // Save to cache with encryption
        await setCachedSales(salesToCache);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch sales:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load sales. Please check your connection.';
        setError(errorMessage);
      } finally {
        if (isReload) {
          setReloading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      includeVoided,
      salesService,
      setCachedSales,
      isCompanyInitialized,
      activeCompanyId,
    ],
  );

  // Only fetch data if cache is checked, company is initialized, and no cached data exists
  useEffect(() => {
    if (
      cacheChecked &&
      cacheInitialized &&
      isCompanyInitialized &&
      !cachedSales
    ) {
      void fetchSales();
    }
  }, [
    cacheChecked,
    cacheInitialized,
    isCompanyInitialized,
    cachedSales,
    fetchSales,
    includeVoided,
  ]);

  const reload = useCallback(() => {
    void fetchSales(true);
  }, [fetchSales]);

  const updateSale = useCallback(
    (updatedSale: Sales) => {
      setSales((prevSales) => {
        const nextSales = prevSales.map((sale) =>
          sale.id === updatedSale.id ? updatedSale : sale,
        );

        const sortedSales = sortSalesByDateDesc(nextSales);

        // Update cache with encrypted data (fire and forget)
        void setCachedSales(sortedSales);

        return sortedSales;
      });
    },
    [setCachedSales],
  );

  return {
    sales,
    loading,
    reloading,
    error,
    reload,
    updateSale,
  };
}
